/**
 * Validate YAML Fragment Tool — "Sinks Not Pipes"
 *
 * Parses a YAML snippet and validates it against the canonical Zod schema for
 * the named schema type. Returns actionable errors with "did you mean?"
 * suggestions for the most common drift patterns seen across 5 raft postmortems.
 *
 * Otters should call this BEFORE writing YAML to disk — catching drift in-session
 * is orders of magnitude cheaper than a failed prebuild after the raft completes.
 */
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import { load as yamlLoad } from 'js-yaml';
import {
  WorkflowStepSchema,
  WorkflowDefinitionSchema,
  WorkflowFieldSchema,
  WorkflowActionSchema,
  authConfigSchema,
} from '@stackwright-pro/types';

// ---------------------------------------------------------------------------
// Supported schema names
// ---------------------------------------------------------------------------

export const SUPPORTED_SCHEMA_NAMES = [
  'workflow_step',
  'workflow_definition',
  'workflow_field',
  'workflow_action',
  'navigation_item',
  'auth_config',
] as const;

export type SchemaName = (typeof SUPPORTED_SCHEMA_NAMES)[number];

// ---------------------------------------------------------------------------
// Inline navigation schema (not exported from @stackwright-pro/types —
// mirrors @stackwright/types NavigationItem shape without adding the dep)
// ---------------------------------------------------------------------------

const NavigationLinkSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    label: z.string().min(1),
    href: z.string().min(1),
    children: z.array(NavigationLinkSchema).optional(),
  })
);

const NavigationSectionSchema = z.object({
  section: z.string().min(1),
  items: z.array(NavigationLinkSchema),
});

const NavigationItemSchema = z.union([
  NavigationLinkSchema as z.ZodObject<any>,
  NavigationSectionSchema,
]);

// ---------------------------------------------------------------------------
// Known field-name drift patterns from 5 raft postmortems.
// Key: wrong field name → correct usage hint.
// ---------------------------------------------------------------------------

export const FIELD_SYNONYMS: Record<SchemaName, Record<string, string>> = {
  workflow_step: {
    title: 'label',
    name: 'label (at step level, use label: not name:)',
    description: 'message (use message: for step-level descriptive text)',
    style: 'theme.variant — use theme: { variant: "primary" } not style: "primary"',
    transitions:
      'on_submit.transition — use on_submit: { transition: "next_step" } not transitions: [{then:...}]',
  },
  workflow_definition: {
    title: 'label',
    name: 'label (workflow-level label, not name:)',
    description: 'description is valid here — but message: is not (that is a step field)',
    type: 'id (workflows are identified by id:, not type:)',
  },
  workflow_field: {
    id: 'name (field-level identifier is name:, NOT id:)',
    title: 'label (field display text is label:, NOT title:)',
    description: 'placeholder or label (no description field on WorkflowField)',
  },
  workflow_action: {
    style: 'theme.variant — use theme: { variant: "primary" } not style: "primary"',
    then: 'transition (action-level next-step is transition:, NOT then:)',
    on_click: 'action: "service:..." (extract the service reference from on_click.action)',
    title: 'label (action display text is label:, NOT title:)',
    name: 'label (action display text is label:, NOT name:)',
  },
  navigation_item: {
    children:
      'items (NavigationSection uses items: not children: — also use section: instead of label: for section headers)',
    label:
      'section (if this is a nav group/section, use section: "Title" and items: [...], not label:)',
    title: 'section (NavigationSection header field is section:, NOT title:)',
  },
  auth_config: {
    method: 'type — auth config discriminates on type: "oidc" | "pki", NOT method:',
    provider_type: 'type — use type: "oidc" or type: "pki"',
    strategy: 'type — top-level discriminator is type:, not strategy:',
  },
};

// ---------------------------------------------------------------------------
// Schema registry
// ---------------------------------------------------------------------------

function getSchema(name: SchemaName): z.ZodTypeAny {
  switch (name) {
    case 'workflow_step':
      return WorkflowStepSchema;
    case 'workflow_definition':
      return WorkflowDefinitionSchema;
    case 'workflow_field':
      return WorkflowFieldSchema;
    case 'workflow_action':
      return WorkflowActionSchema;
    case 'navigation_item':
      return NavigationItemSchema;
    case 'auth_config':
      return authConfigSchema;
  }
}

// ---------------------------------------------------------------------------
// Validation error enrichment
// ---------------------------------------------------------------------------

export interface ValidationError {
  path: string;
  message: string;
  didYouMean?: string;
}

export interface ValidateYamlFragmentInput {
  schemaName: string;
  yaml: string;
}

export interface ValidateYamlFragmentResult {
  valid: boolean;
  errors?: ValidationError[];
  parseError?: string;
}

function enrichErrors(issues: z.ZodIssue[], synonyms: Record<string, string>): ValidationError[] {
  return issues.map((issue) => {
    const pathStr = issue.path.join('.');
    // Check if the last path segment is a known synonym
    const lastSegment = issue.path[issue.path.length - 1];
    const fieldName = typeof lastSegment === 'string' ? lastSegment : undefined;
    const didYouMean = fieldName ? synonyms[fieldName] : undefined;
    return {
      path: pathStr || '(root)',
      message: issue.message,
      ...(didYouMean ? { didYouMean } : {}),
    };
  });
}

// ---------------------------------------------------------------------------
// Handler (exported for direct testing)
// ---------------------------------------------------------------------------

export function handleValidateYamlFragment(
  input: ValidateYamlFragmentInput
): ValidateYamlFragmentResult {
  const { schemaName, yaml } = input;

  // Guard: unknown schema name
  if (!(SUPPORTED_SCHEMA_NAMES as readonly string[]).includes(schemaName)) {
    return {
      valid: false,
      parseError: `Unknown schemaName: "${schemaName}". Supported: ${SUPPORTED_SCHEMA_NAMES.join(', ')}`,
    };
  }

  const name = schemaName as SchemaName;

  // Parse YAML
  let parsed: unknown;
  try {
    parsed = yamlLoad(yaml);
  } catch (err) {
    return {
      valid: false,
      parseError: `YAML parse error: ${err instanceof Error ? err.message : String(err)}`,
    };
  }

  // Validate against Zod schema
  const schema = getSchema(name);
  const result = schema.safeParse(parsed);

  if (result.success) {
    return { valid: true };
  }

  const synonyms = FIELD_SYNONYMS[name];
  const errors = enrichErrors(result.error.issues, synonyms);
  return { valid: false, errors };
}

// ---------------------------------------------------------------------------
// MCP tool registration
// ---------------------------------------------------------------------------

export function registerValidateYamlFragmentTool(server: McpServer): void {
  server.tool(
    'stackwright_pro_validate_yaml_fragment',
    'Validates a YAML snippet against a canonical Zod schema before writing to disk. Returns actionable errors with "did you mean?" suggestions for common field-name drift (title vs label, id vs name, style vs theme.variant, etc.). Call this before stackwright_pro_safe_write to catch schema violations in-session.',
    {
      schemaName: z
        .enum(SUPPORTED_SCHEMA_NAMES)
        .describe(
          'Schema to validate against. One of: workflow_step, workflow_definition, workflow_field, workflow_action, navigation_item, auth_config'
        ),
      yaml: z.string().describe('YAML string to validate (can also be JSON — js-yaml parses both)'),
    },
    async ({ schemaName, yaml }) => {
      const result = handleValidateYamlFragment({ schemaName, yaml });
      return {
        content: [{ type: 'text', text: JSON.stringify(result, null, 2) }],
      };
    }
  );
}
